import React, { useState, useEffect } from 'react';
import FeedbackDrawer from './FeedbackDrawer';
import { Box, Typography, Button, useTheme, alpha, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import config from './config';

const Home = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [mainPages, setMainPages] = useState([]);
  const [sliderImages, setSliderImages] = useState([]);
  const [current, setCurrent] = useState(0);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackOrder, setFeedbackOrder] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    // Fetch slider images
    fetch(`${config.API_BASE_URL}/RestaurantInfo/slider`)
      .then(res => res.json())
      .then(data => setSliderImages(data))
      .catch(() => setSliderImages([]));

    // Fetch main pages for button labels
    fetch(`${config.API_BASE_URL}/main-pages`)
      .then(res => res.json())
      .then(data => setMainPages(data))
      .catch(() => setMainPages([]));
  }, []);

  const nextImage = () => setCurrent((current + 1) % sliderImages.length);

  // Auto-slide every 10 seconds
  useEffect(() => {
    if (sliderImages.length === 0) return;
    const timer = setInterval(() => {
      setCurrent((c) => (c + 1) % sliderImages.length);
    }, 10000);
    return () => clearInterval(timer);
  }, [sliderImages]);

  // Automatically open FeedbackDrawer if any order is in Feedback stage
  useEffect(() => {
    let user = null;
    try {
      user = JSON.parse(localStorage.getItem('user'));
    } catch {}
    const customerId = user?.Id;
    if (!customerId) return;

    fetch(`${config.API_BASE_URL}/Orders/with-customer-id`)
      .then(res => res.json())
      .then(orders => {
        if (!Array.isArray(orders)) return;
        const userOrders = orders.filter(o => o.CustomerId === customerId || o.customerId === customerId);
        const feedbackOrder = userOrders.find(o => (o.OrderStatus || o.status || o.Status) === 'Feedback');
        if (feedbackOrder) {
          setFeedbackOrder(feedbackOrder);
          setFeedbackOpen(true);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <>
      <FeedbackDrawer
        open={feedbackOpen}
        order={feedbackOrder}
        onClose={() => setFeedbackOpen(false)}
        onSubmit={() => {}}
      />
      <Box 
        onClick={(e) => {
          if (e.target.closest('button')) return;
          nextImage();
        }}
        sx={{
          width: '100%',
          // ✅ RESPONSIVE HEIGHT
          height: { xs: 400, sm: 500, md: 650 },
          position: 'relative',
          overflow: 'hidden',
          color: 'primary.contrastText',
          mb: 0,
          // ✅ REMOVED NEGATIVE MARGIN - This was breaking sticky positioning
          mt: 0,
          boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.1)}`,
          cursor: 'pointer'
        }}
      >
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
          {sliderImages.map((img, index) => (
            <Box
              key={index}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: current === index ? 1 : 0,
                transition: 'opacity 1s ease-in-out',
                zIndex: current === index ? 1 : 0,
                pointerEvents: current === index ? 'auto' : 'none',
              }}
            >
              {/* Background Image */}
              <Box sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                backgroundImage: `url(${img.ImagePath})`,
                backgroundPosition: 'center',
                backgroundSize: 'cover',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: `linear-gradient(to bottom, ${alpha(theme.palette.common.black, 0.1)} 0%, ${alpha(theme.palette.common.black, 0.6)} 100%)`,
                }
              }} />

              {/* Content */}
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                px: { xs: 2, sm: 3, md: 4 }, // ✅ Responsive padding
                textAlign: 'center',
                zIndex: 1,
              }}>
                {/* Title */}
                <Typography 
                  variant="h2" 
                  sx={{ 
                    fontWeight: 900, 
                    mb: { xs: 1.5, sm: 2 }, // ✅ Responsive margin
                    textShadow: '0 4px 12px rgba(0,0,0,0.5)',
                    // ✅ RESPONSIVE FONT SIZE
                    fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3.5rem', lg: '4rem' },
                    color: 'custom.white',
                    transform: current === index ? 'translateY(0)' : 'translateY(30px)',
                    opacity: current === index ? 1 : 0,
                    transition: 'all 0.8s ease-out 0.2s',
                    // ✅ Prevent text overflow
                    maxWidth: '90%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {img.Title}
                </Typography>

                {/* Subtitle */}
                <Typography 
                  variant="h5" 
                  sx={{ 
                    mb: { xs: 2, sm: 3, md: 4 }, // ✅ Responsive margin
                    textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                    fontWeight: 500,
                    maxWidth: { xs: '90%', sm: '80%', md: 600 }, // ✅ Responsive max-width
                    color: 'custom.white',
                    // ✅ RESPONSIVE FONT SIZE
                    fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                    transform: current === index ? 'translateY(0)' : 'translateY(30px)',
                    opacity: current === index ? 1 : 0,
                    transition: 'all 0.8s ease-out 0.4s',
                    // ✅ Prevent text overflow
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {img.Subtitle}
                </Typography>

                {/* CTA Button */}
                {img.Action && (
                  <Button
                    variant="contained"
                    size={isMobile ? 'medium' : 'large'} // ✅ Responsive size
                    sx={{ 
                      // ✅ RESPONSIVE FONT SIZE
                      fontSize: { xs: '0.9rem', sm: '1rem', md: '1.2rem' },
                      // ✅ RESPONSIVE PADDING
                      px: { xs: 3, sm: 4, md: 6 },
                      py: { xs: 1, sm: 1.25, md: 1.5 },
                      borderRadius: 50, 
                      bgcolor: 'primary.main', 
                      color: 'custom.white', 
                      '&:hover': { 
                        bgcolor: 'primary.dark',
                        transform: 'scale(1.05)' // ✅ Added hover effect
                      },
                      transform: current === index ? 'translateY(0)' : 'translateY(30px)',
                      opacity: current === index ? 1 : 0,
                      transition: 'all 0.8s ease-out 0.6s',
                      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`, // ✅ Better shadow
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      navigate(img.Action);
                    }}
                  >
                    {(() => {
                      const pageName = mainPages.find(p => p.PagePath === img.Action)?.PageName;
                      return pageName ? t(`pages.${pageName}`) : img.Action;
                    })()}
                  </Button>
                )}
              </Box>
            </Box>
          ))}
          
          {/* Indicators */}
          <Box sx={{ 
            position: 'absolute', 
            bottom: { xs: 20, sm: 30, md: 40 }, // ✅ Responsive bottom position
            left: '50%', 
            transform: 'translateX(-50%)', 
            display: 'flex', 
            gap: { xs: 1, sm: 1.5 }, // ✅ Responsive gap
            zIndex: 2 
          }}>
            {sliderImages.map((_, index) => (
              <Box
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrent(index);
                }}
                sx={{
                  // ✅ RESPONSIVE INDICATOR SIZE
                  width: current === index 
                    ? { xs: 40, sm: 50, md: 60 }
                    : { xs: 20, sm: 25, md: 30 },
                  height: { xs: 4, sm: 5, md: 6 },
                  bgcolor: current === index 
                    ? 'primary.main' 
                    : alpha(theme.palette.common.white, 0.5),
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  '&:hover': {
                    bgcolor: current === index 
                      ? 'primary.main' 
                      : alpha(theme.palette.common.white, 0.8),
                    transform: 'scale(1.1)', // ✅ Added hover effect
                  }
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Home;
